<template>
  <div class="showroom">
    <div class="header">
      <h2>
        {{ $t('POS.MARKETPLACE.SHOWROOM_SETTINGS') }}
      </h2>
      <div class="actions">
        <a
          v-trackClickEvent="{ name: 'showroom_visit_click' }"
          :href="showroomUrl"
          target="_blank"
          class="btn text-btn icon-btn link-main"
        >
          <Icon icon="outside" />
          {{ $t('POS.VISIT_SHOWROOM') }}
        </a>
      </div>
    </div>
    <div v-if="showroomData" class="showroom-settings">
      <div class="box">
        <div class="box-header">
          <h2>{{ $t('SHOWROOM_ADMIN.HEADING') }}</h2>
        </div>
        <div class="box-content">
          <div>
            <h3>{{ $t('SHOWROOM_ADMIN.PICTURE_AND_USERNAME') }}</h3>
            <AccountNavBottom :display-logout="false" />
          </div>
          <div>
            <h3>{{ $t('SHOWROOM_ADMIN.BANNER.HEADING') }}</h3>
            <ShowroomBanner />
          </div>
          <div>
            <h3>{{ $t('SHOWROOM_ADMIN.DESCRIPTION') }}</h3>
            <LabelInput
              ref="descriptionInput"
              :class="{
                'has-error': showroomValidation.descriptionBlocklist.length,
              }"
            >
              <DebounceTextarea
                name="description"
                :maxlength="showroomValidation.descriptionLength"
                :model-value="showroomData.description"
                rows="3"
                spellcheck="true"
                placeholder="Description"
                @update:model-value="marketplaceStore.setShowroomDescription"
              />
              <div class="field-info">
                <small
                  v-if="showroomValidation.descriptionBlocklist.length"
                  class="error-info error-info-description"
                >
                  {{ $t('DESIGNS.VALIDATION.BLACKLIST.TERMS') }}:
                  {{ showroomValidation.descriptionBlocklist.join(', ') }}
                </small>
                <small v-else class="info">
                  {{ $t('SHOWROOM_ADMIN.DESCRIPTION_HINT') }}
                </small>
                <small class="char-info design-description-chars-left"
                  >{{ showroomData.description.length || 0 }} /
                  {{ showroomValidation.descriptionLength }}</small
                >
              </div>
            </LabelInput>
          </div>
          <div>
            <h3>{{ $t('SHOWROOM_ADMIN.SOCIALS.HEADING') }}</h3>
            <ShowroomSocial />
          </div>

          <div
            v-tooltip.top-start="{
              content: isAdmin ? 'not allowed as admin' : null,
              hideOnTargetClick: false,
            }"
            class="save-actions"
          >
            <ProgressButton
              class="btn btn-primary btn-lg"
              :label="$t('GENERAL.SAVE')"
              :on-click="save"
              :disabled="isAdmin || !hasChanged"
            >
            </ProgressButton>
            <button
              :disabled="isAdmin || !hasChanged"
              class="link-main"
              @click="reset"
            >
              {{ $t('GENERAL.REVERT') }}
            </button>
          </div>
        </div>
      </div>
      <div class="box optout-box">
        <div class="box-header">
          <h2>{{ $t('SHOWROOM_ADMIN.AI_OPT_OUT.HEADING') }}</h2>
          <p class="text-sm text-grey">
            {{ $t('SHOWROOM_ADMIN.AI_OPT_OUT.SUBHEADING') }}
          </p>
        </div>
        <div class="box-content">
          <AiOptOut />
        </div>
      </div>
      <div class="box delete-box">
        <div class="box-header">
          <h2>{{ $t('SHOWROOM_ADMIN.DELETE.HEADING') }}</h2>
          <p class="text-sm text-grey">
            {{ $t('SHOWROOM_ADMIN.DELETE.SUBHEADING') }}
          </p>
        </div>
        <div class="box-content">
          <DeleteShowroom />
        </div>
      </div>
    </div>
    <div v-else class="loading-wrapper">
      <LoadingHeart />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import {
  useMarketplaceStore,
  ValidationErrorType,
} from 'commons/store/marketplace';
import DebounceTextarea from 'src/app/components/input/DebounceTextarea.vue';
import LabelInput from 'commons/labelInput/LabelInput.vue';
import LoadingHeart from 'commons/LoadingHeart/LoadingHeart.vue';
import { useStore } from 'vuex';
import ProgressButton from 'commons/btnProgress/ProgressButton.vue';
import localeService from 'commons/localeService/localeService';
import DeleteShowroom from './DeleteShowroom.vue';
import AccountNavBottom from '../../account/AccountNavBottom.vue';
import ShowroomBanner from './ShowroomBanner.vue';
import ShowroomSocial from './ShowroomSocial.vue';
import unsavedChangesService from 'commons/unsavedChangesService/unsavedChangesService';
import { useToastsStore } from 'commons/stores/toasts';
import { i18n } from 'commons/translate/i18n';
import AiOptOut from './AiOptOut.vue';
import analyticsService from 'commons/tracking/analytics';

const { t } = i18n.global;
const store = useStore();
const marketplaceStore = useMarketplaceStore();
const toastsStore = useToastsStore();
const showroomData = computed(() => marketplaceStore.showroomData);
const hasChanged = computed(() => marketplaceStore.hasChanged);
const showroomValidation = marketplaceStore.validation;
const descriptionInput = ref<InstanceType<typeof LabelInput> | null>(null);

const isAdmin = store.getters['user/isAdmin'];

const showroomUrl = `${localeService.getSprdUrl()}/shop/user/${store.state.user.data.id}`;

const save = async () => {
  try {
    const userLinks = showroomData.value?.userLinks || [];
    await marketplaceStore.saveShowroomData();

    if (userLinks.length > 0) {
      const enabledTypes = userLinks.map((link) => link.type);
      analyticsService.logEvent('showroom_social_media_save', {
        count: userLinks.length,
        types: enabledTypes.join(','),
      });
    }

    toastsStore.addToast({
      variant: 'success',
      text: t('SHOWROOM_ADMIN.SAVE_SUCCESS'),
      hideIcon: true,
      dismissible: true,
      timeout: 8000,
    });
  } catch (error: unknown) {
    if (
      error instanceof Error &&
      error.message === ValidationErrorType.BLOCKLIST_VALIDATION &&
      descriptionInput.value?.$el
    ) {
      descriptionInput.value.$el.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
    throw error;
  }
};

const reset = async () => {
  await marketplaceStore.resetShowroomData();
};

onMounted(async () => {
  await marketplaceStore.fetchShowroomData();

  unsavedChangesService.registerComponent({
    check: () => hasChanged.value,
    save,
    discard: reset,
  });
});
</script>

<style lang="scss" scoped>
.showroom {
  display: flex;
  flex-direction: column;

  .header {
    height: 64px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    padding-inline: 24px;
    margin-bottom: 2px;
    background-color: #fff;
    justify-content: space-between;

    h2 {
      margin: 0;
    }

    .actions {
      display: flex;

      & > * {
        margin-left: 16px;
      }
    }
  }

  .showroom-settings {
    padding: 24px;
    overflow-y: auto;
  }

  .save-actions {
    display: flex;
    align-items: center;
    margin-top: 24px;
    position: relative;
    gap: 16px;

    .btn {
      padding: 8px 24px;
      min-width: 120px;
    }
  }

  :deep(.account-nav-bottom) {
    padding: 0;
    display: inline-block;
    min-width: 250px;
  }
}

.box .box-header {
  h2 {
    margin: 0;
  }

  p {
    margin: 4px 0 0 0;
  }
}

.box-content {
  margin-top: 24px;
  max-width: 668px;
  display: flex;
  flex-direction: column;
  gap: 24px;

  h3 {
    margin: 0 0 8px 0;
  }
}

.loading-wrapper {
  padding: 48px;
}
</style>
